var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, Box, FieldLabel, FormField, FormGrid, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { v4 as uuid } from 'uuid';
import { flowsApi } from 'apis/flows';
import { templatesApi } from 'apis/templates';
import { analytics } from 'app/utils/segment';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { FlowType, } from 'fable/flowEditor';
import { ToggleBox, ToggleBoxHead } from 'fable/toggleBox';
import { getSubmitAnalyticsAction } from 'utils/flowEditor/analytics';
import { FlowNodeTypes } from '../../types';
import { AutoReplies } from './components/autoReplies';
import { BusinessHours } from './components/businessHours/businessHours';
import { ScheduleSelect } from './components/scheduleSelect/scheduleSelect';
const scheduleField = 'schedule';
const finalStepOfMigration = 5;
function getPhoneAudioMessage({ name, recurring, announcement, }) {
    return {
        name,
        isRecurring: !!recurring,
        value: {
            announcement: (announcement === null || announcement === void 0 ? void 0 : announcement.url) || '',
            label: (announcement === null || announcement === void 0 ? void 0 : announcement.label) || 'No auto-reply set',
        },
    };
}
function getTemplateAutoReply({ name, recurring, auto_reply_template_id, }) {
    return auto_reply_template_id
        ? {
            name,
            isRecurring: !!recurring,
            value: {
                label: auto_reply_template_id || 'No auto-reply set',
            },
        }
        : null;
}
export function createBusinessHoursModal(flowType) {
    return function BusinessHoursModal({ node, nodeLabel, replaceNode, triggerClose, }) {
        var _a, _b;
        const { isRemovable, payload } = node;
        // `isRemovable` will be set to false if node has just been migrated.
        // The moment it's "Saved" it needs to be changed to true.
        const isMigrated = !isRemovable;
        const isPhoneNode = flowType === FlowType.FLOW_TYPE_CALL;
        const savedHangUpAfterAnnouncement = (_a = payload === null || payload === void 0 ? void 0 : payload.hangUpAfterAnnouncement) !== null && _a !== void 0 ? _a : false;
        const savedScheduleId = (_b = payload === null || payload === void 0 ? void 0 : payload.scheduleId) !== null && _b !== void 0 ? _b : '';
        const [hangUpAfterAnnouncement, setHangUpAfterAnnouncement] = useState(savedHangUpAfterAnnouncement);
        const [scheduleId, setScheduleId] = useState(savedScheduleId);
        const [readFlowSchedule, { data: schedule, isFetching: isScheduleLoading },] = flowsApi.useLazyReadFlowScheduleQuery();
        const [errors, setErrors] = useState({});
        const isChanged = useIsChanged([savedHangUpAfterAnnouncement, hangUpAfterAnnouncement], [savedScheduleId, scheduleId]);
        const openingHours = schedule === null || schedule === void 0 ? void 0 : schedule.opening_hours;
        const { template } = templatesApi.useReadTemplateByIdQuery((schedule === null || schedule === void 0 ? void 0 : schedule.auto_reply_template_id)
            ? { id: schedule.auto_reply_template_id }
            : skipToken, {
            selectFromResult: (_a) => {
                var { data } = _a, rest = __rest(_a, ["data"]);
                return (Object.assign(Object.assign({}, rest), { template: data === null || data === void 0 ? void 0 : data.data }));
            },
        });
        const defaultAudioMessageOrAutoReply = useMemo(() => {
            var _a;
            if (!schedule)
                return null;
            const { announcement } = schedule;
            return isPhoneNode
                ? {
                    name: 'Standard audio message',
                    isRecurring: false,
                    value: {
                        announcement: announcement.url || '',
                        label: (announcement === null || announcement === void 0 ? void 0 : announcement.label) || 'No auto-reply set',
                    },
                }
                : {
                    name: 'Standard auto-reply',
                    isRecurring: false,
                    value: {
                        label: (_a = template === null || template === void 0 ? void 0 : template.name) !== null && _a !== void 0 ? _a : 'No standard auto-reply set',
                    },
                };
        }, [isPhoneNode, schedule, template]);
        const listItems = useMemo(() => openingHours === null || openingHours === void 0 ? void 0 : openingHours.reduce((acc, openingHours) => {
            const { announcement, auto_reply_template_id } = openingHours;
            const reply = isPhoneNode
                ? getPhoneAudioMessage(openingHours)
                : getTemplateAutoReply(openingHours);
            if ((isPhoneNode ? announcement : auto_reply_template_id) &&
                reply) {
                acc.exceptions.push(reply);
            }
            return acc;
        }, {
            default: defaultAudioMessageOrAutoReply,
            exceptions: [],
        }), [
            isPhoneNode,
            openingHours,
            schedule,
            defaultAudioMessageOrAutoReply,
            template,
        ]);
        const validate = useCallback(() => {
            const errors = {};
            if (!scheduleId) {
                errors[scheduleField] = 'Please select a schedule';
            }
            setErrors(errors);
            return Object.keys(errors).length === 0;
        }, [scheduleId]);
        const handleSaveClick = useCallback(() => {
            const isValid = validate();
            if (!isValid) {
                return;
            }
            const { payload } = node;
            if (isMigrated) {
                analytics.businessHoursMigrationWizardFormSubmitted({
                    step: finalStepOfMigration,
                    hangUpAfterAnnouncement,
                });
            }
            else {
                const analyticsEvent = getSubmitAnalyticsAction(!!node.isAction);
                analyticsEvent({
                    flowType,
                    nodeType: FlowNodeTypes.BUSINESS_HOURS,
                    settings: Object.assign(Object.assign({}, ((payload === null || payload === void 0 ? void 0 : payload.scheduleId) !== scheduleId && { scheduleId })), ((payload === null || payload === void 0 ? void 0 : payload.hangUpAfterAnnouncement) !==
                        hangUpAfterAnnouncement && {
                        hangUpAfterAnnouncement,
                    })),
                });
            }
            const newNode = Object.assign(Object.assign({}, node), { nodeType: FlowNodeTypes.BUSINESS_HOURS, text: 'Business hours', icon: 'clock', isRemovable: true, isEditable: true, isAction: false, overlay: [], badges: [], payload: Object.assign(Object.assign({}, payload), { scheduleId,
                    hangUpAfterAnnouncement, closedNodeId: hangUpAfterAnnouncement
                        ? uuid()
                        : (payload === null || payload === void 0 ? void 0 : payload.closedNodeId) || uuid(), openNodeId: (payload === null || payload === void 0 ? void 0 : payload.openNodeId) || uuid() }) });
            triggerClose();
            replaceNode(newNode);
        }, [
            validate,
            scheduleId,
            hangUpAfterAnnouncement,
            isMigrated,
            node,
            replaceNode,
            triggerClose,
        ]);
        const handleScheduleChange = useCallback((id) => {
            const scheduleId = id !== null && id !== void 0 ? id : '';
            setScheduleId(scheduleId);
            readFlowSchedule({ id: scheduleId });
        }, [readFlowSchedule]);
        useEffectOnce(() => {
            if (scheduleId) {
                readFlowSchedule({ id: scheduleId });
            }
        });
        const hasDefaultTemplate = !!(schedule === null || schedule === void 0 ? void 0 : schedule.auto_reply_template_id);
        const hasAnyTemplates = hasDefaultTemplate || (listItems === null || listItems === void 0 ? void 0 : listItems.exceptions.length);
        return (_jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, Object.assign({}, (!isMigrated && { onClose: triggerClose }), { title: nodeLabel }, { children: isMigrated ? (_jsx(Banner, { icon: "route", variant: "announcement", description: _jsx(Text.Paragraph, Object.assign({ variant: "4" }, { children: "Congrats! You can now use this schedule across as many flows as you like. You can always go here to edit your schedules: Settings > Manage > Business hours" })) })) : (_jsx(Text.Paragraph, Object.assign({ subdued: true }, { children: "Decide what happens to incoming conversations based on whether your business is open or closed. You can create schedules by going to Settings > Manage > Business hours > Add new schedule." }))) })), _jsxs("form", Object.assign({ onSubmit: handleSaveClick }, { children: [_jsx(ModalBody, { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(ScheduleSelect, { onChange: handleScheduleChange, error: errors[scheduleField], value: scheduleId }) }), !!scheduleId && (_jsxs(_Fragment, { children: [_jsxs(FormField, { children: [_jsx(FieldLabel, { children: "Business hours" }), _jsx(BusinessHours, { isScheduleSelected: !!scheduleId, openingHours: openingHours })] }), flowType !== FlowType.FLOW_TYPE_MESSENGER && (_jsx(AutoReplies, { isPhoneNode: isPhoneNode, listItems: listItems, isScheduleLoading: isScheduleLoading, hasAnyTemplates: !!hasAnyTemplates, hasDefaultTemplate: hasDefaultTemplate })), isPhoneNode && (_jsx(FormField, { children: _jsx(ToggleBox, { isOpen: hangUpAfterAnnouncement, onChange: setHangUpAfterAnnouncement, headComponent: ToggleBoxHead, title: "Hang up after audio message", input: {
                                                        id: 'hang-up-afer-announcement',
                                                        name: 'Hang Up After Announcement',
                                                        value: 'hang-up-afer-announcement',
                                                    } }) }))] }))] }) }), _jsx(ModalFoot, Object.assign({}, (!isMigrated && { cancelOnClick: triggerClose }), { confirmName: isMigrated ? 'Finish' : getConfirmButtonVerb(node), confirmProps: { disabled: !isChanged && !isMigrated }, confirmOnClick: handleSaveClick }))] }))] })));
    };
}
